import React, { useEffect, useMemo, useState } from "react";
import {
  useGetProductsQuery,
  useCreateProductMutation,
  useDeleteProductMutation,
  useUpdateProductMutation,
  useAddReviewMutation,
} from "../../productApi/productAPI";
import { Product } from "../../types/product";
import { useAppDispatch } from "../../store/hooks/hooks";
import { addToCart } from "../../store/cartSlice/cartSlice";
import {
  Box,
  Text,
  Button,
  Flex,
  Heading,
  Spinner,
  Icon,
  Image,
  Tooltip,
} from "@chakra-ui/react";
import { useAuth } from "../../auth/AuthContext";
import { ShoppingCart } from "lucide-react";
import { keyframes } from "@emotion/react";
import ProductCarousel, { CarouselProduct } from "../Carousel/ProductCarousel";
import ProductCarouselSmall from "../Carousel/ProductsCarouselSmall";
import ProductCarouselTwoColumn from "../Carousel/ProductCarouselTwoColumn";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useNavigate } from "react-router-dom";
import {
  normalizeCategoryLabel,
  getCategoryVisual,
} from "../../services/category/categoryVisuals";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../configs/firebase";
import BusinessesSection from "../Businesses/BusinessesSection";
import PickDenguWhatsAppQr from "../../WhatsappModule/PickDenguWhatsAppQr";
import { FaWhatsapp } from "react-icons/fa";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";
import ClassicProductShowcase, {
  ClassicProductShowcaseProduct,
} from "../Carousel/ClassicProductShowcase";
import iosPhoneImage from "../../assets/images/pickdengu-ios-phone.png";
import androidPhoneImage from "../../assets/images/pickdengu-android-phone.png";
import appStoreBadgeImage from "../../assets/images/app-store-badge.png";
import googlePlayBadgeImage from "../../assets/images/google-play-badge.png";
const cardsBorderRadii = "8px";

const iosPhoneFloat = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50% { transform: translateY(-12px) rotate(-3deg); }
`;

const androidPhoneFloat = keyframes`
  0%, 100% { transform: translateY(0px) rotate(6deg); }
  50% { transform: translateY(-10px) rotate(4deg); }
`;

const slugifyCategory = (value: string = "") =>
  normalizeCategoryLabel(value)
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");

const hexToRgba = (hex: string, alpha: number) => {
  const cleaned = hex.replace("#", "");
  if (cleaned.length !== 6) return `rgba(100,116,139,${alpha})`;

  const bigint = parseInt(cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

type ProductPageProps = {
  onOpenBookTrip?: () => void;
  onOpenDelivery?: () => void;
};

const ProductPage: React.FC<ProductPageProps> = ({
  onOpenBookTrip,
  onOpenDelivery,
}) => {
  const { data: products = [], isLoading } = useGetProductsQuery();
  const [createProduct] = useCreateProductMutation();
  const [deleteProduct] = useDeleteProductMutation();
  const [updateProduct] = useUpdateProductMutation();
  const [addReview] = useAddReviewMutation();
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const navigate = useNavigate();

  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const { theme } = useCustomTheme();

  const [search, setSearch] = useState("");
  const [denguPoints, setDenguPoints] = useState(0);

  const [newProduct, setNewProduct] = useState<Product>({
    title: "",
    price: 0,
    imageUrl: "",
    description: "",
    category: "",
    userId: user?.uid,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewProduct({ ...newProduct, [e.target.name]: e.target.value });
  };

  const isDark = theme.name === "dark";
  const brand = theme.brand;

  const pageText = theme.text;
  const mutedText = theme.textMuted;

  const sectionBg = theme.surface;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;

  const pagePaddingX = ["0px"];
  const pagePaddingTop = ["30px"];

  const africanTimeZone = "Africa/Johannesburg";

  const currentWeekday = useMemo(() => {
    return new Intl.DateTimeFormat("en-ZA", {
      weekday: "long",
      timeZone: africanTimeZone,
    }).format(new Date());
  }, []);

  const firstName = useMemo(() => {
    const rawName =
      user?.displayName?.trim() || user?.email?.split("@")[0] || "Guest";

    return rawName.split(" ")[0];
  }, [user]);

  const greetingTitle = `Happy ${currentWeekday}, ${firstName}`;

  useEffect(() => {
    const fetchUserPoints = async () => {
      if (!user?.uid) {
        setDenguPoints(0);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          setDenguPoints(Number(data?.denguPoints || 0));
        } else {
          setDenguPoints(0);
        }
      } catch (error) {
        console.error("Failed to fetch Dengu points:", error);
        setDenguPoints(0);
      }
    };

    fetchUserPoints();
  }, [user?.uid]);

  const primaryButtonStyles = {
    color: theme.textOnPrimary,
    bgImage: theme.gradients?.primaryButton || theme.primary,
    boxShadow: theme.shadowSm,
    _hover: {
      bgImage: theme.gradients?.primaryButtonHover || theme.primaryHover,
      transform: "translateY(-1px)",
      boxShadow: theme.shadowMd,
    },
    _active: { transform: "translateY(0)" },
    borderRadius: "5px",
    transition: "all 0.2s ease",
  };

  const secondaryButtonStyles = {
    color: pageText,
    bg: isDark ? "rgba(255,255,255,0.04)" : "white",
    border: `1px solid ${borderColor}`,
    _hover: {
      bg: isDark ? "rgba(255,255,255,0.07)" : "rgba(42,44,59,0.03)",
      borderColor: borderStrong,
    },
    borderRadius: "5px",
    transition: "all 0.2s ease",
  };

  const sectionTitleColor = pageText;

  const iosAppUrl =
    process.env.REACT_APP_IOS_APP_URL ||
    "https://apps.apple.com/us/app/pickdengu/id6805660939";
  const androidTestUrl =
    process.env.REACT_APP_ANDROID_TEST_URL ||
    "https://play.google.com/apps/testing/mw.pickdengu.app";

  const getProductImage = (product: any) =>
    product?.imageUrl ||
    (Array.isArray(product?.images) && product.images[0]) ||
    product?.thumbnail ||
    "";

  const toCarouselProduct = (product: any): CarouselProduct => ({
    id: String(product?._id || product?.id || product?.slug || product?.title),
    title: product?.title || "Untitled Product",
    image: getProductImage(product),
    description:
      product?.description ||
      `${product?.category || "General"} product available in our marketplace.`,
    price: Number(product?.price || 0),
  });

  const toSmallCarouselProduct = (product: any) => ({
    id: String(product?._id || product?.id || product?.slug || product?.title),
    title: product?.title || "Untitled Product",
    image: getProductImage(product),
    description:
      product?.description ||
      `${product?.category || "General"} product available now.`,
    price: Number(product?.price || 0),
  });

  const normalizedProducts = useMemo(() => {
    return (products || []).filter((p: any) => !!p?.title);
  }, [products]);

  const classicShowcaseProducts = useMemo<
    ClassicProductShowcaseProduct[]
  >(() => {
    return normalizedProducts
      .map((product: any) => ({
        id: String(
          product?._id || product?.id || product?.slug || product?.title || "",
        ),
        title: product?.title || "Untitled Product",
        category: normalizeCategoryLabel(product?.category || "General"),
        image: getProductImage(product),
        description:
          product?.description ||
          `${product?.category || "General"} product available in our marketplace.`,
        price: product?.price,
      }))
      .filter((product) => Boolean(product.id));
  }, [normalizedProducts]);

  const filteredProducts = useMemo(
    () =>
      normalizedProducts.filter((product: any) =>
        String(product?.title || "")
          .toLowerCase()
          .includes(search.toLowerCase()),
      ),
    [normalizedProducts, search],
  );

  const promotionsProducts = useMemo(() => {
    const featured = normalizedProducts.filter(
      (p: any) => p?.isFeatured === true,
    );
    const source = featured.length > 0 ? featured : normalizedProducts;
    return source.slice(0, 10).map(toCarouselProduct);
  }, [normalizedProducts]);

  const winterDealsProducts = useMemo(() => {
    const winterLike = normalizedProducts.filter((p: any) => {
      const tags = Array.isArray(p?.tags)
        ? p.tags.map((t: any) => String(t).toLowerCase())
        : [];
      const category = String(p?.category || "").toLowerCase();
      const title = String(p?.title || "").toLowerCase();

      return (
        tags.includes("winter") ||
        title.includes("winter") ||
        category.includes("fashion") ||
        category.includes("clothing") ||
        category.includes("shoes") ||
        category.includes("appliances") ||
        category.includes("electronic") ||
        category.includes("jackets")
      );
    });

    const source = winterLike.length > 0 ? winterLike : normalizedProducts;
    return source.slice(0, 12).map(toSmallCarouselProduct);
  }, [normalizedProducts]);

  const farmingProducts = useMemo(() => {
    const farmLike = normalizedProducts.filter((p: any) => {
      const category = String(p?.category || "").toLowerCase();
      const title = String(p?.title || "").toLowerCase();
      const tags = Array.isArray(p?.tags)
        ? p.tags.map((t: any) => String(t).toLowerCase())
        : [];

      return (
        category.includes("agriculture") ||
        category.includes("farm") ||
        category.includes("irrigation") ||
        tags.includes("farm") ||
        tags.includes("agriculture") ||
        title.includes("farm")
      );
    });

    const source = farmLike.length > 0 ? farmLike : normalizedProducts;
    return source.slice(0, 12).map(toSmallCarouselProduct);
  }, [normalizedProducts]);

  const exploreCategories = useMemo(() => {
    const source = search.trim() ? filteredProducts : normalizedProducts;

    const grouped = new Map<string, any[]>();

    source.forEach((product: any) => {
      const rawCategory = normalizeCategoryLabel(
        product?.category || "General",
      );
      if (!grouped.has(rawCategory)) grouped.set(rawCategory, []);
      grouped.get(rawCategory)?.push(product);
    });

    const result = Array.from(grouped.entries())
      .map(([title, items]) => {
        const visual = getCategoryVisual(title);
        const Icon = visual.icon;
        const slug = slugifyCategory(title);

        return {
          id: slug,
          title,
          image: getProductImage(items[0]),
          description: `Browse ${title}`,
          meta: `${items.length} product${items.length !== 1 ? "s" : ""}`,
          navigateTo: `/shop/category/${slug}`,
          leftIcon: <Icon size={16} />,
          leftIconColor: visual.color,
          leftIconBg: hexToRgba(visual.color, 0.14),
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));

    console.log("Home page grouped categories:", result);

    return result;
  }, [filteredProducts, normalizedProducts, search]);

  const categoryCards = useMemo(() => {
    const grouped = new Map<string, any[]>();

    normalizedProducts.forEach((product: any) => {
      const category = normalizeCategoryLabel(product?.category || "General");
      if (!grouped.has(category)) grouped.set(category, []);
      grouped.get(category)?.push(product);
    });

    return Array.from(grouped.entries()).map(([title, items]) => ({
      title,
      image: getProductImage(items[0]),
      productCount: items.length,
      description: `Shop the latest ${title} products`,
    }));
  }, [normalizedProducts]);

  const handleDelete = (id: string) => deleteProduct(id);

  const handleAddToCart = (product: Product) => {
    dispatch(addToCart(product));
  };

  if (isLoading)
    return (
      <Flex
        height="100%"
        justifyContent="center"
        alignItems="center"
        width="100%"
        minH="220px"
      >
        <GlobalSpinner />
      </Flex>
    );

  const SectionBlock = ({
    title,
    children,
    mt,
  }: {
    title: string;
    children: React.ReactNode;
    mt?: any;
  }) => (
    <Box mt={mt} pt="10px">
      <Heading
        pb={["10px", "13px", "14px"]}
        px={pagePaddingX}
        lineHeight={["1.4rem", "1.6rem", "1.75rem"]}
        fontSize={["1.25rem", "1.35rem", "1.5rem"]}
        fontWeight="600"
        color={sectionTitleColor}
      >
        {title}
      </Heading>

      <Box
        mx={pagePaddingX}
        mb={2}
        borderRadius={cardsBorderRadii}
        bg={sectionBg}
        boxShadow={theme.shadowSm}
        overflow="hidden"
      >
        <Box
          h="3px"
          bgGradient={`linear(to-r, ${brand.electricBlue}, ${brand.indigo}, ${brand.violet})`}
          opacity={0.9}
        />
        <Box pt={2} pb={2}>
          {children}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box color={pageText} px={{ base: "0", md: "15px" }}>
      <Flex
      mt={'15px'}
        pt={0}
        pb={["6px", "8px", "10px"]}
        borderRadius={cardsBorderRadii}
        bg={theme.gradients?.shellBg || sectionBg}
        p={{ base: 4, md: 6 }}
        border={`1px solid ${borderColor}`}
        justifyContent="space-between"
        alignItems="center"
        gap={{ base: 6, lg: 8 }}
        direction={{ base: "column", lg: "row" }}
        overflow="hidden"
        position="relative"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="3px"
          bgGradient={`linear(to-r, ${brand.electricBlue}, ${brand.indigo}, ${brand.violet})`}
          opacity={0.95}
        />

        <Box flex="1" w="100%">
          <Heading
            fontWeight="700"
            lineHeight="1.05"
            letterSpacing="-0.03em"
            fontSize={["1.8rem", "2.2rem", "3rem"]}
            color={pageText}
            maxW="720px"
          >
            {greetingTitle}
          </Heading>
          <Box mt={4} borderRadius={cardsBorderRadii} maxW="680px">
            <Text
              fontWeight="800"
              color={pageText}
              fontSize={{ base: "sm", md: "md" }}
            >
              Shop faster on WhatsApp
            </Text>

            <Text mt={1} color={mutedText} fontSize={{ base: "sm", md: "md" }}>
              Scan the QR code or click the WhatsApp button to open PickDengu,
              send{" "}
              <Box as="span" fontWeight="800" color={pageText}>
                Hi
              </Box>
              , and start shopping from the menu.
            </Text>
          </Box>

          <Flex
            gap={3}
            mt={5}
            width="100%"
            flexWrap={{ base: "wrap", md: "wrap" }}
          >
            <Button
              {...primaryButtonStyles}
              leftIcon={<ShoppingCart size={14} />}
              borderRadius={theme.radii.sm}
              onClick={onOpenBookTrip}
              size={{ base: "sm", md: "md" }}
              h={{ base: "40px", md: "40px" }}
              minH={{ base: "40px", md: "40px" }}
              px={{ base: 3, md: 4 }}
              fontSize={{ base: "xs", md: "sm" }}
              flex={{ base: "1 1 0", md: "0 0 auto" }}
              minW={{ base: "110px", md: "auto" }}
              whiteSpace="nowrap"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              lineHeight="1"
              sx={{
                "& .chakra-button__icon": {
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 0,
                },
              }}
            >
              <Box as="span" position="relative" top="-1px" lineHeight="1">
                Book a trip
              </Box>
            </Button>

            <Button
              as="a"
              href={`${
                process.env.REACT_APP_PUBLIC_API_BASE_URL ||
                "https://api.pickdengu.mw"
              }/api/v1/whatsapp/qr/start?source=website&campaign=homepage`}
              target="_blank"
              rel="noopener noreferrer"
              leftIcon={
                <Icon
                  color={isDark ? "#86EFAC" : "#15803D"}
                  as={FaWhatsapp as any}
                />
              }
              borderRadius={theme.radii.sm}
              size={{ base: "sm", md: "md" }}
              h={{ base: "40px", md: "40px" }}
              minH={{ base: "40px", md: "40px" }}
              px={{ base: 3, md: 4 }}
              fontSize={{ base: "xs", md: "sm" }}
              flex={{ base: "0 0 auto", md: "0 0 auto" }}
              maxW="100%"
              whiteSpace="nowrap"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              lineHeight="1"
              bg={isDark ? "rgba(34, 197, 94, 0.01)" : "#ECFDF5"}
              color={isDark ? "#86EFAC" : "#15803D"}
              border="1px solid"
              borderColor={isDark ? "rgba(34, 197, 94, 0.32)" : "#BBF7D0"}
              _hover={{
                bg: isDark ? "rgba(34, 197, 94, 0.22)" : "#DCFCE7",
                borderColor: isDark ? "rgba(34, 197, 94, 0.48)" : "#86EFAC",
                color: isDark ? "#BBF7D0" : "#166534",
                boxShadow: theme.shadowMd,
              }}
              _active={{
                bg: isDark ? "rgba(34, 197, 94, 0.18)" : "#BBF7D0",
              }}
              sx={{
                "& .chakra-button__icon": {
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 0,
                },
              }}
            >
              <Box as="span" position="relative" top="-1px" lineHeight="1">
                Shop via WhatsApp
              </Box>
            </Button>
          </Flex>
        </Box>

        <Box
          w={{ base: "100%", lg: "auto" }}
          display={{ base: "none", md: "flex" }}
          justifyContent="center"
        >
          <PickDenguWhatsAppQr
            source="website"
            campaign="homepage"
            title="Shop via WhatsApp"
            description="Scan or click to open PickDengu and start shopping from the menu."
          />
        </Box>
      </Flex>

      <SectionBlock title="Promotions" mt={["10px", "14px", "18px"]}>
        <ProductCarousel products={promotionsProducts} />
      </SectionBlock>

      <SectionBlock title="Winter Deals" mt={["10px", "14px", "18px"]}>
        <ProductCarouselSmall products={winterDealsProducts} />
      </SectionBlock>

      <SectionBlock title="Explore our Store" mt={["10px", "14px", "18px"]}>
        <ProductCarouselTwoColumn
          products={exploreCategories}
          slidesToShow={{ base: 2, sm: 3, md: 5, lg: 6, xl: 7 }}
          itemRoutePrefix="/shop/category"
        />
      </SectionBlock>

      <SectionBlock title="Farming Solutions" mt={["10px", "14px", "18px"]}>
        <ProductCarouselSmall products={farmingProducts} />
      </SectionBlock>

      <SectionBlock title="Businesses & Sellers" mt={["10px", "14px", "18px"]}>
        <BusinessesSection
          title="Businesses & Sellers"
          subtitle="Browse the businesses behind the listings and shop directly from their profiles."
        />
      </SectionBlock>

      <SectionBlock
        title="One shop. Infinite possibilities."
        mt={["10px", "14px", "18px"]}
      >
        <ClassicProductShowcase
          products={classicShowcaseProducts}
          eyebrow="Picked for you"
          description="Explore random products from two different categories, with fresh discoveries each time you visit."
          viewAllLabel="View all products"
          viewAllPath="/products"
        />
      </SectionBlock>

      <SectionBlock title="Get the App" mt={["10px", "14px", "18px"]} >
        <Box px={{ base: 2, md: 6 }} py={{ base: 3, md: 4 }} overflow="visible">
          <Flex
            direction={{ base: "column", lg: "row" }}
            align="center"
            justify="space-between"
            gap={{ base: 6, md: 8, lg: 10 }}
          >
            <Box flex="1" w="100%" maxW="620px">
              <Heading
                color={pageText}
                fontSize={{ base: "1.45rem", md: "1.9rem", lg: "2.15rem" }}
                lineHeight="1.08"
                letterSpacing="-0.025em"
              >
                One app. Endless possibilities.
              </Heading>

              <Text
                mt={3}
                color={mutedText}
                fontSize={{ base: "sm", md: "md" }}
                lineHeight="1.75"
                maxW="570px"
              >
                The iOS app is live and ready to use. Android is currently in
                testing, and you can join the test build today.
              </Text>

              <Flex
                mt={5}
                gap={{ base: 2, sm: 4, md: 5 }}
                direction="row"
                align="stretch"
                justify="flex-start"
                flexWrap="nowrap"
                w="100%"
              >
                <Tooltip
                  label="Open this link on an iPhone or iPad to view PickDengu in the App Store."
                  hasArrow
                  placement="top"
                  openDelay={250}
                >
                  <Box
                    as="a"
                    href={iosAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    flex={{ base: "1 1 0", sm: "0 1 auto" }}
                    w={{ base: 0, sm: "auto" }}
                    minW={0}
                    maxW={{ base: "none", sm: "220px", md: "230px" }}
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    cursor="pointer"
                    textDecoration="none"
                    transition="all 0.2s ease"
                    _hover={{
                      transform: "translateY(-2px)",
                    }}
                  >
                    <Image
                      src={appStoreBadgeImage}
                      alt="Download PickDengu on the App Store"
                      display="block"
                      h={{ base: "44px", sm: "48px", md: "52px" }}
                      w={{ base: "100%", sm: "auto" }}
                      maxW="100%"
                      objectFit={{ base: "fill", sm: "contain" }}
                      draggable={false}
                      userSelect="none"
                    />

                    <Flex
                      align="center"
                      justify="center"
                      gap={2}
                      mt={2}
                      px={3}
                      py={1.5}
                      w={{ base: "100%", sm: "auto", md: "156px" }}
                      borderRadius="999px"
                      border={`1px solid ${
                        isDark ? "rgba(134,239,172,0.26)" : "#BBF7D0"
                      }`}
                      bg={isDark ? "rgba(34,197,94,0.10)" : "#F0FDF4"}
                      color={isDark ? "#86EFAC" : "#15803D"}
                      fontSize="xs"
                      fontWeight="800"
                      lineHeight="1"
                      transition="all 0.2s ease"
                      _groupHover={{
                        bg: isDark ? "rgba(34,197,94,0.16)" : "#DCFCE7",
                      }}
                    >
                      <Box
                        w="7px"
                        h="7px"
                        flexShrink={0}
                        borderRadius="full"
                        bg={isDark ? "#86EFAC" : "#16A34A"}
                      />
                      <Text as="span">Live on App Store</Text>
                    </Flex>
                  </Box>
                </Tooltip>

                <Tooltip
                  label="Open this link on an Android device to join the PickDengu test on Google Play."
                  hasArrow
                  placement="top"
                  openDelay={250}
                >
                  <Box
                    as="a"
                    href={androidTestUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    flex={{ base: "1 1 0", sm: "0 1 auto" }}
                    w={{ base: 0, sm: "auto" }}
                    minW={0}
                    maxW={{ base: "none", sm: "220px", md: "230px" }}
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    cursor="pointer"
                    textDecoration="none"
                    transition="all 0.2s ease"
                    _hover={{
                      transform: "translateY(-2px)",
                    }}
                  >
                    <Image
                      src={googlePlayBadgeImage}
                      alt="Join the PickDengu Android test on Google Play"
                      display="block"
                      h={{ base: "44px", sm: "48px", md: "52px" }}
                      w={{ base: "100%", sm: "auto" }}
                      maxW="100%"
                      objectFit={{ base: "fill", sm: "contain" }}
                      draggable={false}
                      userSelect="none"
                    />

                    <Flex
                      align="center"
                      justify="center"
                      gap={2}
                      mt={2}
                      px={3}
                      py={1.5}
                      w={{ base: "100%", sm: "auto", md: "168px" }}
                      borderRadius="999px"
                      border={`1px solid ${
                        isDark ? "rgba(103,232,249,0.24)" : "#A5F3FC"
                      }`}
                      bg={isDark ? "rgba(8,145,178,0.10)" : "#ECFEFF"}
                      color={isDark ? "#67E8F9" : "#0891B2"}
                      fontSize="xs"
                      fontWeight="800"
                      lineHeight="1"
                      transition="all 0.2s ease"
                    >
                      <Box
                        w="7px"
                        h="7px"
                        flexShrink={0}
                        borderRadius="full"
                        bg={isDark ? "#67E8F9" : "#0891B2"}
                      />
                      <Text as="span">Join Android test</Text>
                    </Flex>
                  </Box>
                </Tooltip>
              </Flex>
            </Box>

            <Flex
              flex="1"
              w="100%"
              minH={{ base: "390px", sm: "420px", md: "455px", lg: "485px" }}
              maxW={{ base: "570px", lg: "610px" }}
              position="relative"
              align="center"
              justify="center"
              mt={{ base: 1, lg: 0 }}
              px={{ base: 2, sm: 2, md: 2 }}
              py={{ base: 2, sm: 2, md: 2 }}
              boxSizing="border-box"
              overflow="visible"
            >
              <Box
                position="absolute"
                left={{ base: "7%", sm: "8%", md: "9%" }}
                top={{ base: "28px", sm: "32px", md: "36px" }}
                w={{ base: "49%", sm: "47%", md: "46%" }}
                zIndex={2}
                animation={`${iosPhoneFloat} 6s ease-in-out infinite`}
                transformOrigin="center"
              >
                <Box
                  as="a"
                  href={iosAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  display="block"
                  cursor="pointer"
                  transition="filter 0.2s ease"
                  _hover={{
                    filter: "brightness(1.04)",
                  }}
                >
                  <Image
                    src={iosPhoneImage}
                    alt="PickDengu iOS app"
                    w="100%"
                    h="auto"
                    objectFit="contain"
                    draggable={false}
                    userSelect="none"
                    filter={
                      isDark
                        ? "drop-shadow(0 22px 28px rgba(0,0,0,0.36))"
                        : "drop-shadow(0 22px 28px rgba(15,23,42,0.16))"
                    }
                  />
                </Box>
              </Box>

              <Box
                position="absolute"
                right={{ base: "7%", sm: "8%", md: "9%" }}
                bottom={{ base: "28px", sm: "32px", md: "36px" }}
                w={{ base: "49%", sm: "47%", md: "46%" }}
                zIndex={1}
                animation={`${androidPhoneFloat} 6.6s ease-in-out infinite`}
                transformOrigin="center"
              >
                <Box
                  as="a"
                  href={androidTestUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  display="block"
                  cursor="pointer"
                  transition="filter 0.2s ease"
                  _hover={{
                    filter: "brightness(1.04)",
                  }}
                >
                  <Image
                    src={androidPhoneImage}
                    alt="PickDengu Android test app"
                    w="100%"
                    h="auto"
                    objectFit="contain"
                    draggable={false}
                    userSelect="none"
                    filter={
                      isDark
                        ? "drop-shadow(0 22px 28px rgba(0,0,0,0.36))"
                        : "drop-shadow(0 22px 28px rgba(15,23,42,0.16))"
                    }
                  />
                </Box>
              </Box>
            </Flex>
          </Flex>
        </Box>
      </SectionBlock>
    </Box>
  );
};

export default ProductPage;
